'use client'

import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'

const loadGlobeScene = () => import('./GlobeScene')
const GlobeScene = dynamic(loadGlobeScene, { ssr: false })

const INTERACTION_EVENTS = ['pointerdown', 'pointermove', 'wheel', 'touchstart', 'keydown', 'scroll'] as const

/** Safety net for visitors who never touch the page; long enough to sit outside any load measurement. */
const FALLBACK_MS = 15000

/**
 * Delay after the load event before starting the scene when nobody has interacted yet. Phones get a
 * longer one: their CPUs need several seconds after load to finish hydrating, and the scene setup
 * (~0.3 s of main-thread work on a mid-range phone) would otherwise land right in that window.
 */
const IDLE_START_MS = 1500
const IDLE_START_MS_MOBILE = 5000

const idle = (cb: () => void) =>
  'requestIdleCallback' in window ? requestIdleCallback(cb, { timeout: 1000 }) : setTimeout(cb, 50)

/**
 * True when WebGL is unavailable or only software-rendered (SwiftShader / llvmpipe: no GPU, or a
 * blocklisted one). Software rasterising the globe, bloom and glass shell pins the main thread for
 * many seconds, so those visitors keep the static gradient backdrop instead.
 */
function lacksHardwareWebGL() {
  try {
    const gl = document.createElement('canvas').getContext('webgl2') ?? document.createElement('canvas').getContext('webgl')
    if (!gl) return true
    const info = gl.getExtension('WEBGL_debug_renderer_info')
    const renderer = String(gl.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : gl.RENDERER))
    gl.getExtension('WEBGL_lose_context')?.loseContext()
    return /swiftshader|llvmpipe|software|basic render/i.test(renderer)
  } catch {
    return true
  }
}

/**
 * The 3D scene (Three.js + a multi-MB model) is heavy main-thread work, so it starts shortly
 * after page load (or on the visitor's first interaction, if sooner) instead of during it. Until then the page shows the
 * gradient backdrop, so the server-rendered hero (LCP), TBT and INP stay untouched.
 */
export default function GlobeSceneLazy() {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let started = false
    const start = () => {
      if (started) return
      started = true
      cleanup()
      if (lacksHardwareWebGL()) return
      // Split the start into separate tasks: evaluate the Three.js chunk first, then mount (WebGL
      // context, scene, shaders) in a later idle slot, instead of one long main-thread block.
      loadGlobeScene()
        .then(() => idle(() => setReady(true)))
        .catch(() => setReady(true))
    }
    const cleanup = () => {
      INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, start))
      clearTimeout(timer)
      window.removeEventListener('load', onLoad)
    }
    INTERACTION_EVENTS.forEach((e) => window.addEventListener(e, start, { passive: true, once: true }))
    const timer = setTimeout(start, FALLBACK_MS)
    // Setting up WebGL blocks the main thread for ~1 s. Started by the first scroll, that freeze hits
    // right as the camera should begin moving, so start it in the first idle slot after load instead.
    const mobile = window.matchMedia('(pointer: coarse), (max-width: 767px)').matches
    const onLoad = () => setTimeout(() => idle(start), mobile ? IDLE_START_MS_MOBILE : IDLE_START_MS)
    if (document.readyState === 'complete') onLoad()
    else window.addEventListener('load', onLoad, { once: true })
    return cleanup
  }, [])

  return (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 40%, #1d1240 0%, #120c2b 45%, var(--flagship-bg) 100%)',
        }}
      />
      {ready && <GlobeScene />}
      {/* legibility scrim between the globe and page text */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          background:
            'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(11,11,20,0.62) 0%, rgba(11,11,20,0.3) 60%, rgba(11,11,20,0) 100%)',
        }}
      />
    </>
  )
}
